import React from 'react'
import { ActivityIndicator, Pressable, ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native'
import { useMutation } from '@tanstack/react-query'
import type { UserResponse } from '@warcarp/api-types'
import { VACATION_MAX_DAYS, VACATION_MESSAGE_MAX, VACATION_MIN_DAYS } from '@warcarp/api-types'
import { AppModal } from '../ui/AppModal'
import { Button } from '../ui/Button'
import { useAuth } from '../../hooks/useAuth'
import { apiDelete, apiPatch, apiPost } from '../../lib/api'
import { isVacationActive, vacationMessage, vacationRemainingText } from '../../lib/vacation'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'
import { VacationStatusChip } from './VacationStatusChip'
import { SavedBadge } from './SavedBadge'


export function VacationSettingsModal({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { user, refreshUser } = useAuth()
  const [vacation, setVacation] = React.useState(user?.vacation ?? null)
  const [days, setDays] = React.useState(VACATION_MIN_DAYS)
  const [message, setMessage] = React.useState('')
  const savedMessage = React.useRef('')
  const initialized = React.useRef(false)
  const [sessionReady, setSessionReady] = React.useState(false)
  const messageRef = React.useRef(message)
  messageRef.current = message
  const [saveState, setSaveState] = React.useState<'idle' | 'saved' | 'saving' | 'error'>('idle')
  const hideSaved = React.useCallback(() => setSaveState(current => current === 'saved' ? 'idle' : current), [])
  const [now, setNow] = React.useState(Date.now())
  const active = isVacationActive(vacation, now)
  const mutation = useMutation({
    scope: { id: 'player-vacation' },
    mutationFn: async (action: { kind: 'start' | 'update' | 'end'; message?: string }) => {
      if (action.kind === 'start') return apiPost<UserResponse>('/api/v1/users/me/vacation', { days, message: action.message })
      if (action.kind === 'end') return apiDelete<UserResponse>('/api/v1/users/me/vacation')
      return apiPatch<UserResponse>('/api/v1/users/me/vacation', { message: action.message })
    },
    onSuccess: async (result, action) => {
      if (action.kind === 'start' || action.kind === 'end') {
        // Keep the current form through the account refresh, then dismiss it
        // together with the updated account tile. Never flash the opposite CTA.
        await refreshUser()
        onClose()
        return
      }
      setVacation(result.vacation ?? null)
      savedMessage.current = action.message ?? result.vacation?.message ?? ''
      setSaveState(messageRef.current === savedMessage.current ? 'saved' : 'saving')
      await refreshUser()
    },
    onError: () => setSaveState('error'),
  })
  const mutateVacation = mutation.mutate
  const vacationPending = mutation.isPending
  React.useEffect(() => {
    if (!visible) { initialized.current = false; setSessionReady(false); return }
    if (initialized.current || !user) return
    initialized.current = true
    const initial = user.vacation ?? null
    setVacation(initial)
    setMessage(initial?.message ?? '')
    savedMessage.current = initial?.message ?? ''
    setSaveState('idle')
    setNow(Date.now())
    setSessionReady(true)
  }, [visible, user])
  React.useEffect(() => {
    if (!visible) return
    const interval = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(interval)
  }, [visible])
  React.useEffect(() => {
    if (!visible || !sessionReady || !active || message === savedMessage.current || vacationPending || saveState === 'error') return
    setSaveState('saving')
    const timer = setTimeout(() => mutateVacation({ kind: 'update', message }), 2000)
    return () => clearTimeout(timer)
  }, [visible, sessionReady, active, message, vacationPending, mutateVacation, saveState])
  const close = () => {
    if (mutation.isPending) return
    if (active && message !== savedMessage.current) {
      mutation.mutate({ kind: 'update', message }, { onSuccess: onClose })
    } else onClose()
  }
  useWebModalHistory({ visible, canDismiss: !mutation.isPending, onDismiss: close })
  const endVacation = () => mutation.mutate({ kind: 'end' })
  return <AppModal visible={visible} transparent animationType="fade" onRequestClose={close}>
    <View className="flex-1 items-center justify-center px-5" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <Pressable testID="vacation-settings-backdrop" accessibilityRole="button" accessibilityLabel="Dismiss vacation settings"
        onPress={close} disabled={mutation.isPending} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }} />
      <View className="bg-canvas border border-olive rounded-2xl w-full" style={{ maxWidth: 440, maxHeight: '90%' }}>
        <View className="flex-row items-center justify-between px-4 py-1 border-b border-olive" style={{ gap: 8 }}>
          <Text accessibilityRole="header" className="font-stencil text-ink text-base flex-1">Vacation mode</Text>
          <TouchableOpacity onPress={close} disabled={mutation.isPending} accessibilityLabel="Close vacation settings" accessibilityRole="button" style={{ padding: 8 }}>
            <Text className="text-ink text-xl">×</Text>
          </TouchableOpacity>
        </View>
        <ScrollView testID="vacation-settings-scroll" keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 20, gap: 16 }}>
          {!sessionReady ? <ActivityIndicator accessibilityLabel="Loading vacation settings" /> : <>
          {active && <View className="flex-row items-center gap-3">
            <VacationStatusChip />
            <Text className="text-ink font-typed text-base font-semibold text-center flex-1">{vacationRemainingText(vacation!.endsAt, now)} remaining</Text>
          </View>}
          <Text className="text-ink font-typed text-sm">While vacation mode is on, you can’t be booted for inactivity or replaced by a bot. Your turn timer starts fresh when vacation mode ends.</Text>
          {!active && <View className="flex-row items-center justify-between">
            <Text className="text-ink font-typed">Days away</Text>
            <View className="flex-row items-center gap-4">
              <Button title="−" variant="secondary" onPress={() => setDays(days - 1)} disabled={days <= VACATION_MIN_DAYS} />
              <Text accessibilityLabel={`${days} vacation days`} className="text-ink font-stencil">{days}</Text>
              <Button title="+" variant="secondary" onPress={() => setDays(days + 1)} disabled={days >= VACATION_MAX_DAYS} />
            </View>
          </View>}
          <View className="gap-2">
            <Text className="text-ink font-typed text-sm">Message for other players (optional)</Text>
            <TextInput accessibilityLabel="Vacation message" multiline value={message} onChangeText={text => { const next = vacationMessage(text); setMessage(next); setSaveState(next === savedMessage.current ? 'idle' : 'saving') }}
              className="border border-olive rounded-lg bg-paper text-ink px-3 py-3 font-typed" style={{ minHeight: 84, textAlignVertical: 'top' }} />
            <View className="flex-row items-center justify-between">
              <View style={{ minHeight: 20 }}>
                {active && saveState === 'saved' ? <SavedBadge onDone={hideSaved} visibleMs={5000} label="Saved!" textClassName="font-typed text-sm text-olive-up font-semibold" />
                  : <Text accessibilityLiveRegion="polite" className={`font-typed text-sm ${saveState === 'error' ? 'text-blood font-semibold' : 'text-ink-soft'}`}>{active ? saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Not saved' : '' : ''}</Text>}
              </View>
              <Text className="text-ink-soft font-typed text-xs">{Array.from(message).length}/{VACATION_MESSAGE_MAX}</Text>
            </View>
          </View>
          {mutation.error && <View className="gap-2">
            <Text accessibilityRole="alert" className="text-blood font-typed text-sm">{mutation.error.message}</Text>
            {active && <Button title="Retry saving message" variant="secondary" onPress={() => mutation.mutate({ kind: 'update', message })} disabled={mutation.isPending} />}
          </View>}
          {active ? <Button testID="vacation-settings-submit" title="End vacation" style={{ height: 48 }} textColor="#fff" onPress={endVacation} disabled={mutation.isPending} loading={mutation.isPending && mutation.variables?.kind === 'end'} />
            : <Button testID="vacation-settings-submit" title="Start vacation" style={{ height: 48 }} textColor="#fff" onPress={() => mutation.mutate({ kind: 'start', message })} loading={mutation.isPending} />}
          </>}
        </ScrollView>
      </View>
    </View>
  </AppModal>
}
